import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {FormBuilder} from "@angular/forms";
import { Observable } from "rxjs";
import {AddSingleFormatToCartForm} from "../../../forms/add-single-format-to-cart.form";
import {AddSingleFormatToCartModel} from "../../../models/cart/add-single-format-to-cart.model";
import {AddToCartResultModel} from "../../../models/cart/add-to-cart-result.model";
import {PriceCalculationResultModel} from "../../../models/cart/price-calculation-result.model";
import { ConditionListsAgreementModel } from "../../../models/components/condition-lists-agreement.model";
import { ProductMtaAgreementModel } from "../../../models/components/product-mta-agreement.model";
import {ProductFormatModel} from "../../../models/product/product-format.model";
import { ProductModel } from "../../../models/product/product.model";
import {NotificationService} from "../../../services/components/notification.service";
import { ProductCartQuantityService } from "../../../services/components/product-cart-quantity.service";
import {LoggerService} from "../../../services/logger.service";
import {ProductCartService} from "../../../services/product/product-cart.service";
import { ProductService } from "../../../services/product/product.service";
import {AddToCartControlBaseComponent} from "../add-to-cart-control-base/add-to-cart-control-base.component";
import {takeUntil} from "rxjs/operators";
import { DistributionRuleModel } from "../../../models/cart/distribution-rule.model";
import {DialogService} from "../../../services/components/dialog.service";
import {ConditionListsPopUpService} from "../../../services/components/condition-lists-pop-up.service";
import {ProductMtaPopUpService} from "../../../services/components/product-mta-pop-up.service";

@Component({
    selector: "pcg-ng-add-to-cart-pane",
    template: require('./add-to-cart-pane.component.html')
})
export class AddToCartPaneComponent extends AddToCartControlBaseComponent<AddSingleFormatToCartForm, AddSingleFormatToCartModel> implements OnInit, OnDestroy {
    @Input()
    public product: ProductModel;

    public productFormats: Array<ProductFormatModel> = new Array<ProductFormatModel>();

    @Input()
    public productDescription: string;
    public agreement: ConditionListsAgreementModel;
    public mtaAgreement: ProductMtaAgreementModel;

    public get showDistributionRuleAlert(): boolean {
        return this.redirectRules.length > 0;
    }

    public get directDistributionRuleAvailable(): boolean {
        return this.product.distributionRules.some(r => r.isDirectRule);
    }

    public get redirectRules(): Array<DistributionRuleModel> {
        return this.product.distributionRules.filter(r => !r.isDirectRule && !r.isDepositor);
    }

    public get depositorRules(): Array<DistributionRuleModel> {
        return this.product.distributionRules.filter(r => r.isDepositor);
    }

    public constructor(
        logger: LoggerService,
        formBuilder: FormBuilder,
        productCartService: ProductCartService,
        productService: ProductService,
        notificationService: NotificationService,
        productCartQuantityService: ProductCartQuantityService,
        private dialogService: DialogService,
        private conditionListsPopUpService: ConditionListsPopUpService,
        private productMtaPopUpService: ProductMtaPopUpService
    ) {
        super(formBuilder, productCartService, productService, notificationService, productCartQuantityService);
    }

    public ngOnInit(): void {
        this.productFormats = this.product.formats;
        this.addToCartForm = AddSingleFormatToCartForm.createForm(this.formBuilder, new AddSingleFormatToCartModel(this.product.id));
        this.createSubscriptions();
    }

    public handleSubmitClick(): void {
        this.lock();

        const handleLocalCheckConditions = () => {
            this.productService.checkConditions([this.product.id])
            .pipe(takeUntil(this.destroy$))
            .subscribe(checkResult => {
                if (checkResult.length) {
                    this.agreement = new ConditionListsAgreementModel(
                        this.product,
                        checkResult[0],
                        () => {
                            this.addToCartForm.submit();
                            this.agreement = null;
                        },
                        () => this.agreement = null
                    );

                    this.dialogService.focus();
                } else {
                    this.addToCartForm.submit();
                }
                this.unLock();
            });
        };

        this.productService.checkMta([this.product.id])
            .pipe(takeUntil(this.destroy$))
            .subscribe(checkResult => {
                if (checkResult.length) {
                    this.mtaAgreement = new ProductMtaAgreementModel(
                        this.product,
                        checkResult,
                        () => {
                            handleLocalCheckConditions();
                            this.mtaAgreement = null;
                        },
                        () => this.mtaAgreement = null
                    );

                    this.dialogService.focus();
                } else {
                    handleLocalCheckConditions();
                }
                this.unLock();
            });
    }

    protected createSubscriptions(): void {
        this.addToCartForm.changeObservable
            .pipe(takeUntil(this.destroy$))
            .subscribe(model => this.handleChange(model));
        this.addToCartForm.submitObservable
            .pipe(takeUntil(this.destroy$))
            .subscribe(model => this.handleSubmit(model));
    }

    protected calculatePrice(model: AddSingleFormatToCartModel): Observable<PriceCalculationResultModel> {
        return this.productCartService.calculatePriceBySingleFormat(model);
    }

    protected processCalculationResult(result: PriceCalculationResultModel): void {
        super.processCalculationResult(result);

        this.productFormats = result.formats;
        this.addToCartForm.handleFormatsUpdate(this.productFormats);
    }

    protected addProductToCart(model: AddSingleFormatToCartModel): Observable<AddToCartResultModel> {
        return this.productCartService.addSingleFormatToCart(model);
    }
}
